import React from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import IconButton from "@material-ui/core/IconButton";
import Collapse from "@material-ui/core/Collapse";
import Box from "@material-ui/core/Box";
import FilterListIcon from "@material-ui/icons/FilterList";
import ClearIcon from "@material-ui/icons/Clear";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  panel: {
    margin: "0 8px 4px",
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
    boxShadow: "none",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: 28,
    padding: "2px 8px",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.mode === 'dark' ? '#374151' : '#f9fafb',
    },
    transition: 'background-color 0.2s ease',
  },
  content: {
    padding: theme.spacing(2),
    paddingTop: 0,
  },
  section: {
    marginBottom: theme.spacing(2),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  chipsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
  },
  chip: {
    margin: 0,
    height: '28px',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  clearButton: {
    marginTop: theme.spacing(1),
    display: "flex",
    justifyContent: "center",
  },
  emptyState: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    fontSize: "0.875rem",
  },
}));

const FilterPanel = ({ 
  open, 
  onToggle, 
  selectedQueueIds, 
  selectedTagIds,
  queues,
  tags,
  onClearFilters,
  onRemoveQueue,
  onRemoveTag,
  extraActiveCount = 0,
  hideHeader = false,
  children
}) => {
  const classes = useStyles();
  const theme = useTheme();
  
  const activeFiltersCount = (selectedQueueIds?.length || 0) + (selectedTagIds?.length || 0);
  const badgeCount = activeFiltersCount + (Number(extraActiveCount) || 0);
  
  if (hideHeader && !open) return null;

  return (
    <Paper className={classes.panel} elevation={0} style={hideHeader ? { margin: "0 8px 6px", paddingTop: 12 } : undefined}>
      {!hideHeader && (
      <Box 
        className={classes.header}
        onClick={onToggle}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <FilterListIcon style={{ color: theme.palette.text.secondary, fontSize: 16 }} />
          <Typography variant="caption" style={{ fontWeight: 600, lineHeight: 1 }}>
            {i18n.t("tickets.filterPanel.title")}
          </Typography>
          {badgeCount > 0 && (
            <Chip 
              label={badgeCount} 
              size="small" 
              color="primary"
              style={{ height: '20px', fontSize: '0.7rem', minWidth: '24px' }}
            />
          )}
        </Box>
        <Typography variant="caption" style={{ color: theme.palette.text.secondary, lineHeight: 1, fontSize: 11 }}>
          {open
            ? i18n.t("tickets.filterPanel.hide")
            : i18n.t("tickets.filterPanel.show")}
        </Typography>
      </Box>
      )}
      
      <Collapse in={open}>
        <Box className={classes.content}>
          {children ? <Box className={classes.section}>{children}</Box> : null}
          {/* Sección Departamentos */}
          {(selectedQueueIds?.length > 0) && (
            <Box className={classes.section}>
              <Typography className={classes.sectionTitle}>
                <span role="img" aria-label={i18n.t("tickets.filterPanel.departments")}>📁</span>{" "}
                {i18n.t("tickets.filterPanel.departmentsWithCount", {
                  count: selectedQueueIds.length,
                })}
              </Typography>
              <Box className={classes.chipsContainer}>
                {selectedQueueIds.map(queueId => {
                  const queue = queues?.find(q => q.id === queueId);
                  if (!queue) return null;
                  return (
                    <Chip
                      key={queueId}
                      label={queue.name}
                      onDelete={() => onRemoveQueue(queueId)}
                      className={classes.chip}
                      style={{ 
                        backgroundColor: queue.color || theme.palette.primary.main, 
                        color: '#fff' 
                      }}
                      size="small"
                    />
                  );
                })}
              </Box>
            </Box>
          )}
          
          {/* Sección Etiquetas */}
          {(selectedTagIds?.length > 0) && (
            <Box className={classes.section}>
              <Typography className={classes.sectionTitle}>
                <span role="img" aria-label={i18n.t("tickets.filterPanel.tags")}>🏷️</span>{" "}
                {i18n.t("tickets.filterPanel.tagsWithCount", {
                  count: selectedTagIds.length,
                })}
              </Typography>
              <Box className={classes.chipsContainer}>
                {selectedTagIds.map(tagId => {
                  const tag = tags?.find(t => t.id === tagId);
                  if (!tag) return null;
                  return (
                    <Chip
                      key={tagId}
                      label={tag.name}
                      onDelete={() => onRemoveTag(tagId)}
                      className={classes.chip}
                      style={{ 
                        backgroundColor: tag.color || theme.palette.primary.main, 
                        color: '#fff' 
                      }}
                      size="small"
                    />
                  );
                })}
              </Box>
            </Box>
          )}
          
          {/* Botón Limpiar */}
          {activeFiltersCount > 0 && (
            <Box className={classes.clearButton}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onClearFilters();
                }}
                style={{ color: theme.palette.error.main }}
              >
                <ClearIcon fontSize="small" />
                <Typography variant="caption" style={{ marginLeft: 4 }}>
                  {i18n.t("tickets.filterPanel.clearFilters")}
                </Typography>
              </IconButton>
            </Box>
          )}
          
          {/* Mensaje cuando no hay filtros */}
          {activeFiltersCount === 0 && !hideHeader && (
            <Box className={classes.emptyState}>
              <Typography variant="body2">
                {i18n.t("tickets.filterPanel.emptyState")}
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default FilterPanel;
